import { Box, Grid, InputBase, Typography, Button, Tooltip } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import React from "react";
import { useSearch } from "../../../../hooks";
import { borderColor, useTheme } from "@mui/system";
import Card from "@mui/material/Card";
import { IDataTableV2CommandBarProps } from "./DataTableV2CommandBar.types";
import { DataTableV2Date } from "../../preDefinedPlugins/DataTableV2Date/DataTableV2Date";
import { DatatableV2Refresh } from "../../preDefinedPlugins/DataTableV2Refresh/DataTableV2Refresh";
import { DataTableV2Tabs } from "../TableTabs/DataTableV2Tabs";
import { DataTableV2SearchFilter } from "../../preDefinedPlugins/SearchFilter/SearchFilter";
import { DataTableV2ColumnsVisibility } from "../../preDefinedPlugins/DataTableV2ColumnsVisibility/DataTableV2ColumnsVisibility";

export const DataTableV2CommandBar: React.FC<IDataTableV2CommandBarProps> = (
  props,
) => {
  const { preDefinedPlugins, leftItems, rightItems, centerItems, customCommandBar } =
    props.tableCommandBarProps;
  const {
    masterColumns,
    originalMasterColumns,
    detailColumns = [],
    items,
    tableTabsProps,
    rows,
    masterColumnResize,
    autoColumnWidths = {},
  } = props;
  const theme = useTheme();

  const [isAutoFitted, setIsAutoFitted] = React.useState(false);
  const preAutoFitValuesRef = React.useRef<Record<string, { width: number }>>({});

  const handleAutoFit = React.useCallback(() => {
    if (!masterColumnResize) return;

    if (!isAutoFitted) {
      // Save current state before expanding
      preAutoFitValuesRef.current = { ...(masterColumnResize.values || {}) };

      // Expand: measure all visible cells via DOM and auto-fit
      const newValues: Record<string, { width: number }> = { ...(masterColumnResize.values || {}) };

      masterColumns.forEach((column) => {
        if (!column) return;
        const allCells = document.querySelectorAll(
          `[data-column-key="${column.key}"]`
        );
        let maxContentWidth = 0;
        allCells.forEach((cell) => {
          const inner = cell.firstElementChild as HTMLElement;
          if (inner) {
            const prevOverflow = inner.style.overflow;
            const prevWhiteSpace = inner.style.whiteSpace;
            inner.style.overflow = "visible";
            inner.style.whiteSpace = "nowrap";
            const contentWidth = inner.scrollWidth + 16;
            inner.style.overflow = prevOverflow;
            inner.style.whiteSpace = prevWhiteSpace;
            maxContentWidth = Math.max(maxContentWidth, contentWidth);
          }
        });
        if (maxContentWidth > 0) {
          newValues[column.key] = { width: maxContentWidth };
        }
      });

      masterColumnResize.onResize(newValues);
      setIsAutoFitted(true);
    } else {
      // Shrink: reset — restore pre-autofit state
      masterColumnResize.onResize(preAutoFitValuesRef.current);
      setIsAutoFitted(false);
    }
  }, [isAutoFitted, masterColumns, masterColumnResize, autoColumnWidths]);

  const renderLeftItems = React.useMemo(() => {
    const customPlugins = leftItems.customPlugins;
    let leftNodes: React.ReactNode[] = [];

    if (customPlugins) {
      customPlugins.forEach((customPlugin) => {
        leftNodes.push(
          <Box sx={{ mr: 1 }}>
            {customPlugin.onRender(masterColumns, detailColumns, items)}
          </Box>,
        );
      });
    }

    return leftNodes;
  }, [leftItems, masterColumns, items]);

  const renderRightItems = React.useMemo(() => {
    const customPlugins = rightItems.customPlugins;
    let rightNodes: React.ReactNode[] = [];

    if (customPlugins) {
      customPlugins.forEach((customPlugin) => {
        rightNodes.push(
          <Box>
            {customPlugin.onRender(masterColumns, detailColumns, items)}
          </Box>,
        );
      });
    }

    return rightNodes;
  }, [rightItems, masterColumns, items]);

  const renderCenterItems = React.useMemo(() => {
    const customPlugins = centerItems?.customPlugins;
    let centerNodes: React.ReactNode[] = [];

    if (customPlugins) {
      customPlugins.forEach((customPlugin) => {
        centerNodes.push(
          <Box key={customPlugin.key} sx={{ width: "100%", maxWidth: "400px" }}>
            {customPlugin.onRender(masterColumns, detailColumns, items)}
          </Box>,
        );
      });
    }

    return centerNodes;
  }, [centerItems, masterColumns, items]);

  return (
    <Card
      sx={{
        marginBottom: 0,
        border: 0,
        borderRadius: {
          md: "10px",
          lg: "15px",
          xl: "20px",
        },
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 10px",
        borderBottomLeftRadius: {
          md: 0,
          lg: 0,
          xl: 0,
        },
        borderBottomRightRadius: {
          md: 0,
          lg: 0,
          xl: 0,
        },
        padding: 0,
        m: 0,
      }}
    >
      {tableTabsProps && (
        <Box
          sx={
            {
              // paddingTop: 0.4,
              // [theme.breakpoints.up("md")]: {
              //   paddingTop: 0.4,
              // },
              // [theme.breakpoints.up("lg")]: {
              //   paddingTop: 1,
              // },
              // [theme.breakpoints.up("xl")]: {
              //   paddingTop: 2,
              // },
            }
          }
        >
          <DataTableV2Tabs {...tableTabsProps} />
        </Box>
      )}

      {customCommandBar && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between",
            paddingInline: 1,
            pt: 2,
            paddingBottom: 0,
            // [theme.breakpoints.up("md")]: {
            //   padding: 1,
            //   paddingBottom: 1,
            // },
            // [theme.breakpoints.up("lg")]: {
            //   padding: 1,
            //   paddingBottom: 1,
            // },
            // [theme.breakpoints.up("xl")]: {
            //   padding: 1,
            //   paddingBottom: 1,
            // },
          }}
        >
          {customCommandBar}
        </Box>
      )}

      <Box
        sx={{
          // bgcolor: "red",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "space-between",
          padding: 1,
          paddingBottom: 0,
          // [theme.breakpoints.up("md")]: {
          //   padding: 1,
          //   // pt: 0,
          //   paddingBottom: 0.8,
          // },
          // [theme.breakpoints.up("lg")]: {
          //   padding: 2,
          //   // pt: 0,
          //   paddingBottom: 1.3,
          // },
          // [theme.breakpoints.up("xl")]: {
          //   padding: 3,
          //   // pt: 0,
          //   paddingBottom: 1.5,
          // },
        }}
      >
        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: { xs: "center", md: "flex-start" },
            flexWrap: { xs: "wrap", md: "unset" },
          }}
        >
          {preDefinedPlugins.autoFit && masterColumnResize && (
            <Box width={"fit-content"}>
              <Tooltip title={isAutoFitted ? "Reset Column Widths" : "Auto-Fit Columns"}>
                <Button
                  variant="text"
                  onClick={handleAutoFit}
                  sx={{
                    padding: "0px 0px",
                    minWidth: "unset",
                    [theme.breakpoints.up("md")]: {
                      padding: "2px 2px",
                    },
                    [theme.breakpoints.up("lg")]: {
                      padding: "2px 4px",
                    },
                    [theme.breakpoints.up("xl")]: {
                      padding: "6px 8px",
                    },
                  }}
                >
                  {isAutoFitted ? (
                    <CloseFullscreenIcon
                      color="primary"
                      sx={{
                        fontSize: 16,
                        [theme.breakpoints.up("md")]: {
                          fontSize: 18,
                        },
                        [theme.breakpoints.up("lg")]: {
                          fontSize: 23,
                        },
                        [theme.breakpoints.up("xl")]: {
                          fontSize: 25,
                        },
                      }}
                    />
                  ) : (
                    <FitScreenIcon
                      color="primary"
                      sx={{
                        fontSize: 16,
                        [theme.breakpoints.up("md")]: {
                          fontSize: 18,
                        },
                        [theme.breakpoints.up("lg")]: {
                          fontSize: 23,
                        },
                        [theme.breakpoints.up("xl")]: {
                          fontSize: 25,
                        },
                      }}
                    />
                  )}
                </Button>
              </Tooltip>
            </Box>
          )}
          {renderLeftItems}
          {preDefinedPlugins.dateFilter && (
            <Box>
              <DataTableV2Date
                state={preDefinedPlugins.dateFilter.state}
                onChange={preDefinedPlugins.dateFilter.onChange}
              />
            </Box>
          )}
          {preDefinedPlugins.search && (
            <Box>
              <DataTableV2SearchFilter
                {...preDefinedPlugins.search}
                masterColumns={originalMasterColumns}
              />
            </Box>
          )}
          {preDefinedPlugins.columnVisibility && (
            <Box>
              <DataTableV2ColumnsVisibility
                {...preDefinedPlugins.columnVisibility}
                masterColumns={originalMasterColumns}
              />
            </Box>
          )}
          {preDefinedPlugins.refresh && (
            <Box>
              <DatatableV2Refresh onClick={preDefinedPlugins.refresh.onClick} />
            </Box>
          )}
        </Box>

        {renderCenterItems.length > 0 && (
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flex: 1,
              flexWrap: { xs: "wrap", md: "unset" },
            }}
          >
            {renderCenterItems}
          </Box>
        )}

        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: { xs: "center", lg: "flex-end" },
            flexWrap: { xs: "wrap", md: "unset" },
          }}
        >
          {renderRightItems}
        </Box>
      </Box>

      {/* <Box
        padding={3}
        paddingTop={0}
        paddingBottom={0}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
            flexWrap: { xs: "wrap", md: "unset" },
          }}
        >
       
        </Box>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: { xs: "center", lg: "flex-end" },
            flexWrap: { xs: "wrap", md: "unset" },
          }}
        >
          {renderRightItems}
        </Box>
      </Box> */}
    </Card>
  );
};
